import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    InputAdornment,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { MetaTags } from '../../components/Metatags/Metatags';
import { BrandSharedResources } from '../../components/BrandTeam/ResourceTeamPanel';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ROUTES } from '../../types/global/routes.types';
import { manageOffsiteCampaigns } from '../../redux/buzzbridge/actions';
import {
    managedCampaignsSelector,
    managedCampaignsPaginationSelector,
    gettingManagedCampaignsSelector,
    activeBrandDashboardBrandSelector,
} from '../../redux/buzzbridge/selectors';
import { ICampaign } from '../../redux/buzzbridge/types';
import { getUsersDashboard } from '../../redux/user/actions';
import { CampaignsData, GigsData, SingleContest } from '../../redux/user/types';
import { isContestCampaignEnabled } from './brandCampaignConfig';
import { RootState } from '../../redux/root-reducer';
import { formatDate, formatNumberWithK } from '../../utils/AppUtils';
import { replaceRouteParam } from '../../utils/routesProcessor';
import {
    BRAND_CAMPAIGNS_SCROLL_STATE_KEY,
    BRAND_CAMPAIGNS_TAB_PARAM,
    BRAND_LUXE_PRIMARY,
    BRAND_LUXE_SECONDARY,
    BRAND_SURFACE_CONTAINER,
    BRAND_SURFACE_CONTAINER_LOW,
    type BrandCampaignsTab,
} from './brandDashboardTokens';
import { BrandCampaignsPaginatedList } from './BrandCampaignsPaginatedList';
import { BrandCampaignsListPagination } from './BrandCampaignsListPagination';
import { hasActiveCampaignHostPlanSelector } from '../../redux/campaignHostSubscription/selectors';
import { CampaignThreadUnreadBadge } from '../../components/BuzzBridge/CampaignThreadUnreadBadge';
import { CampaignHostTierPickerModal } from './subscription/CampaignHostTierPickerModal';
import { CampaignHostCheckoutDialog } from './subscription/CampaignHostCheckoutDialog';
import { CampaignHostPaystackRedirectOverlay } from './subscription/CampaignHostPaystackRedirectOverlay';
import { useCampaignHostCheckout } from './subscription/useCampaignHostCheckout';
import { DuplicateCampaignDialog, isUgcCampaignDuplicable } from './DuplicateCampaignDialog';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import type { ICampaignHostPackage } from '../../types/global/campaignHostSubscription.types';

const BRAND_CAMPAIGNS_PAGE_SIZE = 10;

const CAMPAIGNS_TABS: { key: BrandCampaignsTab; label: string }[] = [
    { key: 'ugc', label: 'UGC campaigns' },
    { key: 'contest', label: 'Contest' },
    { key: 'audition', label: 'Auditions' },
];

function parseCampaignsTab(raw: string | null): BrandCampaignsTab {
    if (raw === 'contest' || raw === 'audition') return raw;
    return 'ugc';
}

function statusColor(status?: string) {
    switch (status) {
        case 'published':
        case 'active':
            return '#00fdc1';
        case 'draft':
            return '#ffc965';
        case 'completed':
            return '#767575';
        default:
            return '#a0a0a0';
    }
}

function formatBudget(amount?: number) {
    if (!amount) return '—';
    if (amount >= 1000) return `$${(amount / 1000).toFixed(1)}k`;
    return `$${amount.toLocaleString()}`;
}

type AuditionRow = GigsData & { slug?: string };

function isAuditionExpired(casting_expiration: string | undefined): boolean {
    if (!casting_expiration) return false;
    return new Date(casting_expiration).getTime() < Date.now();
}

const listRowCardSx = {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    alignItems: { sm: 'center' },
    justifyContent: 'space-between',
    gap: 1.5,
    p: 2.5,
    borderRadius: 2,
    bgcolor: BRAND_SURFACE_CONTAINER_LOW,
    border: '1px solid rgba(255,255,255,0.06)',
    cursor: 'pointer',
    transition: 'border-color 0.2s',
    '&:hover': { borderColor: 'rgba(255,201,101,0.15)' },
} as const;

function contestPhaseColor(phase?: string) {
    switch ((phase || '').toLowerCase()) {
        case 'submission':
            return '#00fdc1';
        case 'judging':
            return '#ffc965';
        case 'voting':
            return '#7eb8ff';
        case 'completed':
            return '#767575';
        default:
            return '#a0a0a0';
    }
}

function mapCampaignToContestRow(campaign: CampaignsData): SingleContest {
    const phaseDates = campaign.contest_phase_dates || [];
    const activePhase =
        phaseDates.find(p => p.phase === campaign.contest_phase) ||
        phaseDates.find(p => p.phase === 'submission') ||
        phaseDates[0];

    return {
        _id: campaign._id,
        title: campaign.title,
        description: campaign.description,
        status: String(campaign.status),
        contest_phase: campaign.contest_phase,
        premium: Boolean(campaign.premium),
        slug: campaign.slug || campaign._id,
        feature_image: campaign.feature_image,
        with_fee: campaign.with_fee,
        approved: campaign.approved,
        funded: campaign.funded,
        date_start: activePhase?.date_started || '',
        expiration_date: activePhase?.expiration_date || '',
        submissionCount: campaign.applications ?? 0,
    };
}

function contestDateSubtitle(row: SingleContest): string {
    if (row.date_start && row.expiration_date) {
        return `${formatDate(row.date_start, 'MMM D, YY')} – ${formatDate(row.expiration_date, 'MMM D, YY')}`;
    }
    if (row.expiration_date) {
        return `Ends ${formatDate(row.expiration_date, 'MMM D, YY')}`;
    }
    if (row.date_start) {
        return `Starts ${formatDate(row.date_start, 'MMM D, YY')}`;
    }
    return row.contest_phase ? `Phase: ${row.contest_phase}` : '—';
}

function BrandCampaignsAuditionRow({ row }: { row: AuditionRow }) {
    const navigate = useNavigate();
    const { themeStyle } = useThemeContext();
    const t = theme[themeStyle];
    const tag = row.tags?.[0];
    const chipColor = BRAND_LUXE_SECONDARY;
    const expired = isAuditionExpired(row.casting_expiration);

    return (
        <Box
            onClick={() => {
                const id = row._id || row.slug;
                if (!id) return;
                navigate(replaceRouteParam(ROUTES.BRAND_AUDITION_DETAIL, 'auditionId', id));
            }}
            sx={listRowCardSx}>
            <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5, flexWrap: 'wrap' }}>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 16,
                            color: t.primary_white,
                            opacity: expired ? 0.75 : 1,
                        }}>
                        {row.title}
                    </Typography>
                    {expired ? (
                        <Chip
                            label="Expired"
                            size="small"
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 11,
                                height: 22,
                                bgcolor: 'rgba(255,107,107,0.14)',
                                color: '#ff8f8f',
                            }}
                        />
                    ) : null}
                    {tag ? (
                        <Chip
                            label={tag}
                            size="small"
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 11,
                                height: 22,
                                textTransform: 'capitalize',
                                bgcolor: `${chipColor}18`,
                                color: chipColor,
                            }}
                        />
                    ) : null}
                </Box>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        fontSize: 12,
                        color: t.form_label,
                    }}>
                    {expired ? 'Expired' : 'Expires'} {formatDate(row.casting_expiration, 'MMM D, YY')}
                </Typography>
            </Box>
            <Typography
                sx={{
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    fontSize: 16,
                    color: BRAND_LUXE_PRIMARY,
                    whiteSpace: 'nowrap',
                }}>
                {formatNumberWithK(row.totalApplications ?? 0)}
            </Typography>
        </Box>
    );
}

function BrandCampaignsUgcRow({
    campaign,
    onDuplicate,
}: {
    campaign: ICampaign;
    onDuplicate: (campaign: ICampaign) => void;
}) {
    const navigate = useNavigate();
    const { themeStyle } = useThemeContext();
    const t = theme[themeStyle];
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const canDuplicate = isUgcCampaignDuplicable(campaign);
    const isExpired = canDuplicate && campaign.status !== 'draft';
    const statusLabel = isExpired ? (campaign.status === 'paused' ? 'paused' : 'expired') : campaign.status || 'draft';

    return (
        <Box
            onClick={() => navigate(ROUTES.BRAND_CAMPAIGN_DETAIL.replace(':campaignId', campaign._id))}
            sx={listRowCardSx}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 16, color: t.primary_white }}>
                        {campaign.title}
                    </Typography>
                    <CampaignThreadUnreadBadge count={campaign.unread_messages_total} size="sm" />
                    <Chip
                        label={statusLabel}
                        size="small"
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 11,
                            height: 22,
                            textTransform: 'capitalize',
                            bgcolor: `${statusColor(isExpired ? 'completed' : campaign.status)}18`,
                            color: statusColor(isExpired ? 'completed' : campaign.status),
                        }}
                    />
                </Box>
                {campaign.platforms && campaign.platforms.length > 0 && (
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 12, color: t.form_label }}>
                        {campaign.platforms.join(', ')}
                    </Typography>
                )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 16,
                        color: BRAND_LUXE_PRIMARY,
                        whiteSpace: 'nowrap',
                    }}>
                    {formatBudget(campaign.total_budget)}
                </Typography>
                <IconButton
                    size="small"
                    aria-label="Campaign actions"
                    onClick={e => {
                        e.stopPropagation();
                        setMenuAnchor(e.currentTarget);
                    }}
                    sx={{ color: t.form_label, '&:hover': { color: BRAND_LUXE_PRIMARY } }}>
                    <MoreVertIcon fontSize="small" />
                </IconButton>
                <Menu
                    anchorEl={menuAnchor}
                    open={Boolean(menuAnchor)}
                    onClose={e => {
                        (e as any)?.stopPropagation?.();
                        setMenuAnchor(null);
                    }}
                    onClick={e => e.stopPropagation()}
                    PaperProps={{
                        sx: {
                            bgcolor: BRAND_SURFACE_CONTAINER,
                            border: '1px solid rgba(255,255,255,0.08)',
                            minWidth: 180,
                            '& .MuiMenuItem-root': {
                                color: '#fff',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                                '&.Mui-disabled': {
                                    color: 'rgba(255,255,255,0.35)',
                                    opacity: 1,
                                },
                            },
                        },
                    }}>
                    <MenuItem
                        disabled={!canDuplicate}
                        onClick={e => {
                            e.stopPropagation();
                            setMenuAnchor(null);
                            if (canDuplicate) onDuplicate(campaign);
                        }}
                        sx={{
                            gap: 1,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: 13,
                            color: '#fff',
                        }}>
                        <ContentCopyOutlinedIcon sx={{ fontSize: 16, color: 'inherit' }} />
                        Duplicate
                        {!canDuplicate ? (
                            <Typography
                                component="span"
                                sx={{ ml: 'auto', fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
                                Ended only
                            </Typography>
                        ) : null}
                    </MenuItem>
                </Menu>
            </Box>
        </Box>
    );
}

function BrandCampaignsContestRow({ row }: { row: SingleContest }) {
    const navigate = useNavigate();
    const { themeStyle } = useThemeContext();
    const t = theme[themeStyle];
    const status = row.status || 'draft';
    const phase = row.contest_phase;

    return (
        <Box
            onClick={() => {
                if (!row._id) return;
                navigate(replaceRouteParam(ROUTES.BRAND_CONTEST_MANAGE, 'contestId', row._id));
            }}
            sx={listRowCardSx}>
            <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5, flexWrap: 'wrap' }}>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 16,
                            color: t.primary_white,
                        }}>
                        {row.title}
                    </Typography>
                    <Chip
                        label={status}
                        size="small"
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 11,
                            height: 22,
                            textTransform: 'capitalize',
                            bgcolor: `${statusColor(status)}18`,
                            color: statusColor(status),
                        }}
                    />
                    {phase ? (
                        <Chip
                            label={phase}
                            size="small"
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 11,
                                height: 22,
                                textTransform: 'capitalize',
                                bgcolor: `${contestPhaseColor(phase)}18`,
                                color: contestPhaseColor(phase),
                            }}
                        />
                    ) : null}
                    {row.premium ? (
                        <Chip
                            label="Premium"
                            size="small"
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 11,
                                height: 22,
                                bgcolor: 'rgba(255,201,101,0.12)',
                                color: BRAND_LUXE_PRIMARY,
                            }}
                        />
                    ) : null}
                </Box>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        fontSize: 12,
                        color: t.form_label,
                    }}>
                    {contestDateSubtitle(row)}
                    {row.funded === false && row.status === 'published' ? ' · Prize pool not funded' : ''}
                </Typography>
            </Box>
            <Typography
                sx={{
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    fontSize: 16,
                    color: BRAND_LUXE_PRIMARY,
                    whiteSpace: 'nowrap',
                }}>
                {formatNumberWithK(row.submissionCount ?? 0)}
            </Typography>
        </Box>
    );
}

const statusFilterSelectSx = {
    minWidth: 110,
    bgcolor: 'rgba(255,255,255,0.05)',
    borderRadius: 999,
    fontSize: 13,
    fontFamily: FONT_ENUM.MAIN_MEDIUM,
    color: '#fff',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.08)' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,201,101,0.4)' },
    '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.4)' },
    '& .MuiSelect-select': { py: 1.5, px: 1.5 },
};

const statusFilterMenuProps = {
    PaperProps: {
        sx: {
            bgcolor: '#1a1919',
            border: '1px solid rgba(255,255,255,0.07)',
            borderRadius: 2,
            '& .MuiMenuItem-root': {
                fontSize: 13,
                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                color: '#fff',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                '&.Mui-selected': { bgcolor: 'rgba(255,201,101,0.1)', color: BRAND_LUXE_PRIMARY },
            },
        },
    },
};

const primaryActionButtonSx = {
    px: 3,
    py: 1,
    borderRadius: 999,
    textTransform: 'none',
    fontFamily: FONT_ENUM.MAIN_BOLD,
    fontSize: 14,
    bgcolor: BRAND_LUXE_PRIMARY,
    color: '#392700',
    '&:hover': { bgcolor: '#feb700' },
} as const;

const campaignsSearchFieldSx = {
    '& .MuiOutlinedInput-root': {
        bgcolor: '#262626',
        borderRadius: '5px',
        py: 0.75,
        pl: 1,
        pr: 2,
        fontFamily: FONT_ENUM.MAIN_MEDIUM,
        fontSize: 15,
        color: '#ffffff',
        '& fieldset': { border: 'none' },
        '&:hover fieldset': { border: 'none' },
        '&.Mui-focused fieldset': { border: 'none' },
        '&.Mui-focused': { boxShadow: '0 0 0 1px rgba(255,201,101,0.35)' },
    },
    '& .MuiInputBase-input::placeholder': { color: 'rgba(173,170,170,0.5)', opacity: 1 },
} as const;

function campaignsSearchPlaceholder(tab: BrandCampaignsTab): string {
    switch (tab) {
        case 'contest':
            return 'Search contests by title or keyword…';
        case 'audition':
            return 'Search auditions by title or keyword…';
        default:
            return 'Search campaigns by title or keyword…';
    }
}

export default function BrandCampaignsPage() {
    const { themeStyle } = useThemeContext();
    const t = theme[themeStyle];
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    const activeBrand = useSelector(activeBrandDashboardBrandSelector);
    const ugcCampaigns: ICampaign[] = useSelector(managedCampaignsSelector);
    const ugcPagination = useSelector(managedCampaignsPaginationSelector);
    const ugcLoading = useSelector(gettingManagedCampaignsSelector);

    const gigsBlock = useSelector((s: RootState) => s.user.dashboardData.gigs);
    const contestsBlock = useSelector((s: RootState) => s.user.dashboardData.campaigns);
    const loadingDashboard = useSelector((s: RootState) => s.user.gettingDashboardData);

    const activeTab = parseCampaignsTab(searchParams.get(BRAND_CAMPAIGNS_TAB_PARAM));
    const statusFromUrl = searchParams.get('status') || '';
    const [statusFilter, setStatusFilter] = useState(statusFromUrl);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [ugcPage, setUgcPage] = useState(1);
    const [auditionsPage, setAuditionsPage] = useState(1);
    const [contestsPage, setContestsPage] = useState(1);
    const [listExpanded, setListExpanded] = useState(false);
    const contestCampaignEnabled = isContestCampaignEnabled();
    const hasPlan = useSelector(hasActiveCampaignHostPlanSelector);
    const [tierPickerOpen, setTierPickerOpen] = useState(false);
    const [duplicateTarget, setDuplicateTarget] = useState<ICampaign | null>(null);

    // If subscription data loads and confirms an active plan, close the tier picker
    // (handles the race where it opened before data was ready)
    useEffect(() => {
        if (hasPlan && tierPickerOpen) setTierPickerOpen(false);
    }, [hasPlan, tierPickerOpen]);

    const {
        checkoutOpen,
        setCheckoutOpen,
        beginMonthly,
        beginLtd,
        onPaymentMethodReady,
        checkoutTitle,
        paystackRedirecting,
        redirectProvider,
    } = useCampaignHostCheckout({
        onSubscriptionComplete: () => {
            setTierPickerOpen(false);
            navigate(newAuditionHref);
        },
    });

    const handleAuditionTierMonthly = useCallback(
        (pkg: ICampaignHostPackage) => {
            setTierPickerOpen(false);
            beginMonthly(pkg);
        },
        [beginMonthly],
    );
    const handleAuditionTierLtd = useCallback(
        (pkg: ICampaignHostPackage) => {
            setTierPickerOpen(false);
            beginLtd(pkg);
        },
        [beginLtd],
    );

    const collapseListExpanded = useCallback(() => setListExpanded(false), []);

    const setActiveTab = useCallback(
        (tab: BrandCampaignsTab) => {
            setSearchParams(
                prev => {
                    const next = new URLSearchParams(prev);
                    if (tab === 'ugc') next.delete(BRAND_CAMPAIGNS_TAB_PARAM);
                    else next.set(BRAND_CAMPAIGNS_TAB_PARAM, tab);
                    return next;
                },
                { replace: true },
            );
        },
        [setSearchParams],
    );

    useEffect(() => {
        const legacyScroll = (location.state as Record<string, unknown> | null)?.[BRAND_CAMPAIGNS_SCROLL_STATE_KEY];
        if (legacyScroll === 'contests') {
            setActiveTab('contest');
            navigate(`${location.pathname}?tab=contest`, { replace: true, state: null });
        }
    }, [location.pathname, location.state, navigate, setActiveTab]);

    useEffect(() => {
        const timer = setTimeout(() => setSearch(searchInput.trim()), 350);
        return () => clearTimeout(timer);
    }, [searchInput]);

    useEffect(() => {
        const fromUrl = searchParams.get('status') || '';
        if (fromUrl !== statusFilter) setStatusFilter(fromUrl);
        // Only sync when the URL changes (e.g. deep link from dashboard).
        // eslint-disable-next-line react-hooks/exhaustive-deps -- statusFilter intentional omit
    }, [searchParams]);

    const handleStatusFilterChange = (value: string) => {
        setStatusFilter(value);
        setSearchParams(
            prev => {
                const next = new URLSearchParams(prev);
                if (!value) next.delete('status');
                else next.set('status', value);
                return next;
            },
            { replace: true },
        );
    };

    useEffect(() => {
        if (activeTab !== 'ugc') return;
        dispatch(
            manageOffsiteCampaigns({
                ...(activeBrand?._id && { brand: activeBrand._id }),
                sort: '-createdAt',
                page: ugcPage,
                limit: BRAND_CAMPAIGNS_PAGE_SIZE,
                ...(statusFilter && { status: statusFilter }),
                ...(search && { search }),
            }),
        );
    }, [dispatch, activeBrand?._id, statusFilter, ugcPage, activeTab, search]);

    useEffect(() => {
        if (activeTab !== 'audition') return;
        dispatch(
            getUsersDashboard({
                page: auditionsPage,
                limit: BRAND_CAMPAIGNS_PAGE_SIZE,
                category: 'gigs',
                replaceData: true,
                ...(search && { searchTerm: search }),
            }),
        );
    }, [dispatch, auditionsPage, activeTab, search]);

    useEffect(() => {
        if (activeTab !== 'contest') return;
        dispatch(
            getUsersDashboard({
                page: contestsPage,
                limit: BRAND_CAMPAIGNS_PAGE_SIZE,
                category: 'campaigns',
                replaceData: true,
                ...(search && { searchTerm: search }),
            }),
        );
    }, [dispatch, contestsPage, activeTab, search]);

    useEffect(() => {
        setUgcPage(1);
    }, [statusFilter, activeBrand?._id, search]);

    useEffect(() => {
        setAuditionsPage(1);
        setContestsPage(1);
    }, [search]);

    useEffect(() => {
        collapseListExpanded();
    }, [ugcPage, auditionsPage, contestsPage, activeTab, statusFilter, activeBrand?._id, search, collapseListExpanded]);

    const auditions = useMemo(() => (gigsBlock?.data ?? []) as AuditionRow[], [gigsBlock?.data]);
    const contests = useMemo(
        () => ((contestsBlock?.data ?? []) as CampaignsData[]).map(mapCampaignToContestRow),
        [contestsBlock?.data],
    );

    const auditionsTotalPages = Math.max(1, gigsBlock?.totalPages ?? 1);
    const contestsTotalPages = Math.max(1, contestsBlock?.totalPages ?? 1);
    const ugcTotalPages = Math.max(1, ugcPagination.totalPages);

    const tabLoading =
        activeTab === 'ugc' ? ugcLoading : activeTab === 'audition' ? loadingDashboard : loadingDashboard;

    const newAuditionHref = `${ROUTES.BRAND_CAMPAIGN_NEW}?type=audition`;
    const newContestHref = `${ROUTES.BRAND_CAMPAIGN_NEW}?type=contest`;
    const newUgcHref = `${ROUTES.BRAND_CAMPAIGN_NEW}?type=ugc`;

    const renderEmpty = (message: string) => (
        <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 15, color: t.form_label }}>
                {message}
            </Typography>
        </Box>
    );

    const renderLoading = () => (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: BRAND_LUXE_PRIMARY }} />
        </Box>
    );

    return (
        <MetaTags title="Campaigns | Brand | Thspian" description="Brand campaigns">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Typography
                    sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: { xs: 22, md: 28 }, color: t.primary_white }}>
                    Campaigns
                </Typography>
                <BrandSharedResources />

                <Box
                    sx={{
                        display: 'flex',
                        p: 0.5,
                        bgcolor: BRAND_SURFACE_CONTAINER,
                        borderRadius: 999,
                        width: 'fit-content',
                        maxWidth: '100%',
                        flexWrap: 'wrap',
                        gap: 0.5,
                    }}>
                    {CAMPAIGNS_TABS.map(tab => (
                        <Button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            disableRipple={activeTab === tab.key}
                            sx={{
                                borderRadius: 999,
                                textTransform: 'none',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 13,
                                px: 2.5,
                                py: 0.75,
                                minWidth: 0,
                                bgcolor: activeTab === tab.key ? BRAND_LUXE_PRIMARY : 'transparent',
                                color: activeTab === tab.key ? '#392700' : '#adaaaa',
                                '&:hover': {
                                    bgcolor: activeTab === tab.key ? BRAND_LUXE_PRIMARY : 'rgba(255,255,255,0.06)',
                                    color: activeTab === tab.key ? '#392700' : '#fff',
                                },
                            }}>
                            {tab.label}
                        </Button>
                    ))}
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 1.5,
                    }}>
                    {activeTab === 'ugc' && (
                        <Select
                            size="small"
                            displayEmpty
                            value={statusFilter}
                            onChange={e => handleStatusFilterChange(e.target.value)}
                            sx={statusFilterSelectSx}
                            MenuProps={statusFilterMenuProps}>
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="draft">Draft</MenuItem>
                            <MenuItem value="published">Published</MenuItem>
                            <MenuItem value="active">Active</MenuItem>
                            <MenuItem value="expired">Expired</MenuItem>
                        </Select>
                    )}
                    {activeTab === 'ugc' && (
                        <Button component={Link} to={newUgcHref} variant="contained" sx={primaryActionButtonSx}>
                            New campaign
                        </Button>
                    )}
                    {activeTab === 'contest' && contestCampaignEnabled && (
                        <Button component={Link} to={newContestHref} variant="contained" sx={primaryActionButtonSx}>
                            New Contest
                        </Button>
                    )}
                    {activeTab === 'audition' && (
                        <Button
                            variant="contained"
                            sx={{
                                ...primaryActionButtonSx,
                                '&.Mui-disabled': {
                                    bgcolor: '#6b6050',
                                    color: '#a89880',
                                },
                            }}
                            onClick={() => {
                                if (!hasPlan) {
                                    setTierPickerOpen(true);
                                } else {
                                    navigate(newAuditionHref);
                                }
                            }}>
                            New Audition
                        </Button>
                    )}
                </Box>

                <TextField
                    fullWidth
                    size="small"
                    placeholder={campaignsSearchPlaceholder(activeTab)}
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: '#adaaaa', fontSize: 20 }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={campaignsSearchFieldSx}
                />

                {tabLoading && renderLoading()}

                {!tabLoading &&
                    activeTab === 'ugc' &&
                    ugcCampaigns.length === 0 &&
                    renderEmpty(
                        search
                            ? 'No campaigns match your search.'
                            : statusFilter === 'expired'
                            ? 'No expired or paused campaigns.'
                            : statusFilter === 'active'
                            ? 'No active campaigns right now.'
                            : statusFilter === 'draft'
                            ? 'No draft campaigns.'
                            : statusFilter === 'published'
                            ? 'No published campaigns.'
                            : 'No campaigns yet. Create your first one!',
                    )}

                {!tabLoading && activeTab === 'ugc' && ugcCampaigns.length === 0 && ugcTotalPages > 1 ? (
                    <BrandCampaignsListPagination page={ugcPage} totalPages={ugcTotalPages} onChange={setUgcPage} />
                ) : null}

                {!tabLoading && activeTab === 'ugc' && ugcCampaigns.length > 0 && (
                    <BrandCampaignsPaginatedList
                        items={ugcCampaigns}
                        getItemKey={campaign => campaign._id}
                        renderItem={campaign => (
                            <BrandCampaignsUgcRow campaign={campaign} onDuplicate={c => setDuplicateTarget(c)} />
                        )}
                        page={ugcPage}
                        totalPages={ugcTotalPages}
                        onPageChange={setUgcPage}
                        sectionLabel="campaigns"
                        expanded={listExpanded}
                        onToggleExpanded={() => setListExpanded(v => !v)}
                    />
                )}

                {!tabLoading &&
                    activeTab === 'audition' &&
                    auditions.length === 0 &&
                    renderEmpty(search ? 'No auditions match your search.' : 'No auditions yet.')}

                {!tabLoading && activeTab === 'audition' && auditions.length > 0 && (
                    <BrandCampaignsPaginatedList
                        items={auditions}
                        getItemKey={row => row._id}
                        renderItem={row => <BrandCampaignsAuditionRow row={row} />}
                        page={auditionsPage}
                        totalPages={auditionsTotalPages}
                        onPageChange={setAuditionsPage}
                        sectionLabel="auditions"
                        expanded={listExpanded}
                        onToggleExpanded={() => setListExpanded(v => !v)}
                    />
                )}

                {!tabLoading &&
                    activeTab === 'contest' &&
                    contests.length === 0 &&
                    renderEmpty(
                        search
                            ? 'No contests match your search.'
                            : contestCampaignEnabled
                            ? 'No contests yet. Create one to run a creative competition.'
                            : 'No contests yet.',
                    )}

                {!tabLoading && activeTab === 'contest' && contests.length > 0 && (
                    <BrandCampaignsPaginatedList
                        items={contests}
                        getItemKey={row => row._id}
                        renderItem={row => <BrandCampaignsContestRow row={row} />}
                        page={contestsPage}
                        totalPages={contestsTotalPages}
                        onPageChange={setContestsPage}
                        sectionLabel="contests"
                        expanded={listExpanded}
                        onToggleExpanded={() => setListExpanded(v => !v)}
                    />
                )}
            </Box>

            <CampaignHostPaystackRedirectOverlay open={paystackRedirecting} provider={redirectProvider ?? 'paystack'} />
            <CampaignHostTierPickerModal
                open={tierPickerOpen}
                onClose={() => setTierPickerOpen(false)}
                onSelectMonthly={handleAuditionTierMonthly}
                onSelectLtd={handleAuditionTierLtd}
            />
            <CampaignHostCheckoutDialog
                open={checkoutOpen}
                title={checkoutTitle}
                onClose={() => setCheckoutOpen(false)}
                onPaymentMethodReady={onPaymentMethodReady}
            />
            <DuplicateCampaignDialog
                open={Boolean(duplicateTarget)}
                campaign={duplicateTarget}
                onClose={() => setDuplicateTarget(null)}
                onDuplicated={newId => {
                    dispatch(
                        manageOffsiteCampaigns({
                            ...(activeBrand?._id && { brand: activeBrand._id }),
                            sort: '-createdAt',
                            page: ugcPage,
                            limit: BRAND_CAMPAIGNS_PAGE_SIZE,
                            ...(statusFilter && { status: statusFilter }),
                            ...(search && { search }),
                        }),
                    );
                    navigate(ROUTES.BRAND_CAMPAIGN_DETAIL.replace(':campaignId', newId));
                }}
            />
        </MetaTags>
    );
}
